import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import CTAButton from '../components/CTAButton';
import TrustSignals from '../components/TrustSignals';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiCheck, FiAlertTriangle } = FiIcons;

const GeneralLiabilityPage = () => {
  const coverageAreas = [
    {
      title: "Bodily Injury",
      description: "Customer slips and falls in your store",
      icon: FiAlertTriangle
    },
    {
      title: "Property Damage",
      description: "Accidentally damage client's property during work",
      icon: FiShield
    },
    {
      title: "Personal & Advertising Injury",
      description: "Claims of libel, slander, or copyright infringement",
      icon: FiCheck
    }
  ];

  const businesses = [
    "Contractors & Construction",
    "Restaurants & Food Service", 
    "Retail Stores",
    "Professional Services",
    "Cleaning Services",
    "Beauty & Wellness"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 to-primary-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-60"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80)'
          }}
        ></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              General Liability Insurance: Essential Protection for Every Business
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto"
            >
              Protect your business from customer injuries, property damage, and lawsuits. Starting at just $19/month!
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <CTAButton text="Get Your General Liability Quote Now" size="xl" className="mb-8" />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <TrustSignals className="text-white" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Coverage Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What General Liability Covers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive protection against the most common business risks
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coverageAreas.map((area, index) => (
              <motion.div
                key={area.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={area.icon} className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{area.title}</h3>
                <p className="text-gray-600">{area.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Who Needs General Liability?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Every business that interacts with customers or works on client property needs general liability insurance. It's often required by clients, landlords, and licensing agencies.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {businesses.map((business, index) => (
                  <motion.div
                    key={business}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiCheck} className="h-5 w-5 text-secondary-600 flex-shrink-0" />
                    <span className="text-gray-700">{business}</span>
                  </motion.div>
                ))}
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1 }}
                className="mt-8"
              >
                <CTAButton text="Get Your Quote Now" />
              </motion.div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-primary-50 p-8 rounded-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why You Need It</h3>
              <div className="space-y-4 text-lg text-gray-700">
                <p>• Protects against lawsuits from accidents</p>
                <p>• Required by most commercial leases</p>
                <p>• Often needed for business licenses</p>
                <p>• Covers legal defense costs</p>
                <p>• Builds trust with customers</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Real Examples */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Real-World Examples
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-red-50 p-8 rounded-lg border-l-4 border-red-400"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Without General Liability</h3>
              <p className="text-gray-700 mb-4">
                A customer slips on a wet floor in your store and breaks their arm. They sue for $50,000 in medical bills and lost wages.
              </p>
              <p className="text-red-600 font-semibold">
                You pay the full $50,000 out of pocket, plus legal fees.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-green-50 p-8 rounded-lg border-l-4 border-green-400"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">With General Liability</h3>
              <p className="text-gray-700 mb-4">
                The same customer slips and sues for $50,000. Your general liability insurance handles the claim.
              </p>
              <p className="text-green-600 font-semibold">
                Insurance covers the settlement and legal fees. You pay nothing.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            Don't Risk Your Business Without Protection
          </motion.h2>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl mb-8"
          >
            Get affordable general liability coverage starting at just $19/month. Protect your business today with Next Insurance.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <CTAButton 
              text="Get Your Instant Quote Now" 
              size="xl" 
              className="bg-white text-primary-600 hover:bg-gray-100" 
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GeneralLiabilityPage;